import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

export interface StarlineSession {
  id: number;
  market: number;
  market_name: string;
  session_date: string;
  session_time: string;
  betting_opens_at: string | null;
  betting_closes_at: string | null;
  status: string;
  result_pana: string | null;
  result_digit: string | null;
  result_display: string | null;
  result_declared_at: string | null;
  settled_at: string | null;
  is_betting_open: boolean;
  created_at: string;
  updated_at: string;
}

export interface FetchStarlineSessionsPayload {
  session_date: string;
  market_id?: number;
  status?: string;
  open_only?: boolean;
}

export interface DeclareStarlineResultPayload {
  session_id: number;
  result_pana: string;
  result_digit: string;
}

export interface PreviewStarlineWinnersPayload {
  session_id: number;
  result_pana: string;
  result_digit?: string;
  winner_limit?: number;
}

export interface RevertStarlineOpenBidPayload {
  session_id: number;
  confirm: true;
}

export interface RevertClosedStarlineBidPayload {
  session_id: number;
  confirm: true;
}

export interface SettleStarlineSessionPayload {
  session_id: number;
}

interface StarlineSessionsResponse {
  success: boolean;
  count: number;
  sessions: StarlineSession[];
}

interface DeclareStarlineResultResponse {
  success: boolean;
  message: string;
  session: StarlineSession;
}

export interface StarlineWinner {
  bet_id: number;
  user_id: number | null;
  user_name: string;
  mobile_number: string | null;
  market_id: number;
  market_name: string | null;
  session_id: number;
  bet_code: string;
  bet_type_id: number | null;
  bet_type_name: string | null;
  selected_number: string;
  amount: string;
  estimated_payout: string;
  placed_at: string | null;
}

export interface StarlineWinnerPreviewData {
  session_id: number;
  market_id: number;
  market_name: string;
  session_date: string;
  session_time: string;
  pana: string;
  digit: string;
  total_bets: number;
  winning_bets: number;
  losing_bets: number;
  total_stake: string;
  winning_stake: string;
  estimated_payout: string;
  winner_count_returned: number;
  winner_limit: number;
  winners_truncated: boolean;
  winners: StarlineWinner[];
}

interface StarlineWinnerPreviewResponse {
  success: boolean;
  message: string;
  data: StarlineWinnerPreviewData;
}

export interface StarlineActualWinner {
  id: number;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  mobile_number?: string | null;
  bet_type_name?: string | null;
  bet_code?: string | null;
  selected_number: string;
  amount: string;
  winning_amount: string;
  status: string;
}

export interface StarlineActualWinnersData {
  session_id: number;
  market_id: number;
  market_name: string;
  session_date: string;
  session_time: string;
  result_pana: string | null;
  result_digit: string | null;
  total_winners: number;
  total_winning_amount: string;
  winners: StarlineActualWinner[];
}

interface StarlineActualWinnersResponse {
  success: boolean;
  message: string;
  data: StarlineActualWinnersData;
}


interface StarlineOpenBidRevertResponse {
  success: boolean;
  message: string;
  data: {
    session_id: number;
    market_id: number;
    market_name: string;
    session_date: string;
    session_time: string;
    previous_result: string | null;
    status: string;
  };
}

interface StarlineBidRevertResponse {
  success: boolean;
  message: string;
  data: {
    session_id: number;
    market_id: number;
    market_name: string;
    session_date: string;
    session_time: string;
    status: string;
    betting_opens_at: string;
    betting_closes_at: string;
    reopen_minutes: number;
  };
}

interface StarlineSettlementResponse {
  success: boolean;
  message: string;
  settlement: {
    session_id: number;
    status: string;
    total_bets: number;
    winning_bets: number;
    losing_bets: number;
    total_payout: string;
  };
}

interface StarlineResultState {
  sessions: StarlineSession[];
  selectedSession: StarlineSession | null;
  winnerPreview: StarlineWinnerPreviewData | StarlineActualWinnersData | null;
  loading: boolean;
  declaring: boolean;
  previewing: boolean;
  reverting: boolean;
  settling: boolean;
  error: string | null;
  success: string | null;
}

const initialState: StarlineResultState = {
  sessions: [],
  selectedSession: null,
  winnerPreview: null,
  loading: false,
  declaring: false,
  previewing: false,
  reverting: false,
  settling: false,
  error: null,
  success: null,
};

const getErrorMessage = (error: any, fallback: string): string => {
  const responseData = error?.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }

  if (typeof responseData?.error === 'string') {
    return responseData.error;
  }

  if (typeof responseData?.message === 'string') {
    return responseData.message;
  }

  if (typeof responseData?.detail === 'string') {
    return responseData.detail;
  }

  if (responseData?.errors && typeof responseData.errors === 'object') {
    const firstError = Object.values(responseData.errors)
      .flat()
      .find(Boolean);

    if (typeof firstError === 'string') {
      return firstError;
    }
  }

  if (typeof error?.message === 'string') {
    return error.message;
  }

  return fallback;
};

export const fetchStarlineSessions = createAsyncThunk(
  'starlineResult/fetchSessions',
  async (
    payload: FetchStarlineSessionsPayload,
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();

      params.set('session_date', payload.session_date);

      if (payload.market_id) {
        params.set('market_id', String(payload.market_id));
      }

      if (payload.status) {
        params.set('status', payload.status);
      }

      if (payload.open_only) {
        params.set('open_only', 'true');
      }

      const response = await api.get<StarlineSessionsResponse>(
        `/starline/sessions/?${params.toString()}`
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to load Starline sessions.')
      );
    }
  }
);

export const declareStarlineResult = createAsyncThunk(
  'starlineResult/declareResult',
  async (
    payload: DeclareStarlineResultPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post<DeclareStarlineResultResponse>(
        '/starline/declare-result/',
        payload
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to declare the Starline result.')
      );
    }
  }
);

export const previewStarlineWinners = createAsyncThunk(
  'starlineResult/previewWinners',
  async (
    payload: PreviewStarlineWinnersPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post<StarlineWinnerPreviewResponse>(
        '/starline/winner-preview/',
        payload
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to preview Starline winners.')
      );
    }
  }
);

export const fetchStarlineActualWinners = createAsyncThunk(
  'starlineResult/fetchActualWinners',
  async (sessionId: number, { rejectWithValue }) => {
    try {
      const response = await api.get<StarlineActualWinnersResponse>(
        '/starline/actual-winners/',
        { params: { session_id: sessionId } }
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to fetch actual Starline winners.')
      );
    }
  }
);

export const revertStarlineOpenBid = createAsyncThunk(
  'starlineResult/revertOpenBid',
  async (
    payload: RevertStarlineOpenBidPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post<StarlineOpenBidRevertResponse>(
        '/starline/open-bid-revert/',
        payload
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to revert the Starline result.')
      );
    }
  }
);

export const reopenStarlineBid = createAsyncThunk(
  'starlineResult/reopenBid',
  async (
    payload: RevertClosedStarlineBidPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post<StarlineBidRevertResponse>(
        '/starline/bid-revert/',
        payload
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to reopen Starline bidding.')
      );
    }
  }
);

export const settleStarlineSession = createAsyncThunk(
  'starlineResult/settleSession',
  async (
    payload: SettleStarlineSessionPayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post<StarlineSettlementResponse>(
        '/starline/settle-session/',
        payload
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        getErrorMessage(error, 'Failed to settle the Starline session.')
      );
    }
  }
);

const starlineResultSlice = createSlice({
  name: 'starlineResult',
  initialState,
  reducers: {
    clearStarlineResultError: (state) => {
      state.error = null;
    },
    clearStarlineResultSuccess: (state) => {
      state.success = null;
    },
    clearSelectedStarlineSession: (state) => {
      state.selectedSession = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStarlineSessions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStarlineSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.sessions = action.payload.sessions;
      })
      .addCase(fetchStarlineSessions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(declareStarlineResult.pending, (state) => {
        state.declaring = true;
        state.error = null;
        state.success = null;
      })
      .addCase(declareStarlineResult.fulfilled, (state, action) => {
        state.declaring = false;
        state.selectedSession = action.payload.session;
        state.success = action.payload.message;

        state.sessions = state.sessions.map((session) =>
          session.id === action.payload.session.id
            ? action.payload.session
            : session
        );
      })
      .addCase(declareStarlineResult.rejected, (state, action) => {
        state.declaring = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(previewStarlineWinners.pending, (state) => {
        state.previewing = true;
        state.winnerPreview = null;
        state.error = null;
      })
      .addCase(previewStarlineWinners.fulfilled, (state, action) => {
        state.previewing = false;
        state.winnerPreview = action.payload.data;
      })
      .addCase(previewStarlineWinners.rejected, (state, action) => {
        state.previewing = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchStarlineActualWinners.pending, (state) => {
        state.previewing = true;
        state.winnerPreview = null;
        state.error = null;
      })
      .addCase(fetchStarlineActualWinners.fulfilled, (state, action) => {
        state.previewing = false;
        state.winnerPreview = action.payload.data;
      })
      .addCase(fetchStarlineActualWinners.rejected, (state, action) => {
        state.previewing = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(revertStarlineOpenBid.pending, (state) => {
        state.reverting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(revertStarlineOpenBid.fulfilled, (state, action) => {
        state.reverting = false;
        state.winnerPreview = null;
        state.success = action.payload.message;

        state.sessions = state.sessions.map((session) =>
          session.id === action.payload.data.session_id
            ? {
                ...session,
                status: action.payload.data.status,
                result_pana: null,
                result_digit: null,
                result_display: null,
                result_declared_at: null,
                settled_at: null,
              }
            : session
        );

        if (
          state.selectedSession?.id === action.payload.data.session_id
        ) {
          state.selectedSession = null;
        }
      })
      .addCase(revertStarlineOpenBid.rejected, (state, action) => {
        state.reverting = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(reopenStarlineBid.pending, (state) => {
        state.reverting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(reopenStarlineBid.fulfilled, (state, action) => {
        state.reverting = false;
        state.success = action.payload.message;

        state.sessions = state.sessions.map((session) =>
          session.id === action.payload.data.session_id
            ? {
                ...session,
                status: action.payload.data.status,
                betting_opens_at: action.payload.data.betting_opens_at,
                betting_closes_at: action.payload.data.betting_closes_at,
                is_betting_open: true,
              }
            : session
        );
      })
      .addCase(reopenStarlineBid.rejected, (state, action) => {
        state.reverting = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(settleStarlineSession.pending, (state) => {
        state.settling = true;
        state.error = null;
        state.success = null;
      })
      .addCase(settleStarlineSession.fulfilled, (state, action) => {
        state.settling = false;
        state.winnerPreview = null;
        state.success = action.payload.message;

        state.sessions = state.sessions.map((session) =>
          session.id === action.payload.settlement.session_id
            ? {
                ...session,
                status: 'settled',
                settled_at: new Date().toISOString(),
              }
            : session
        );

        if (
          state.selectedSession?.id ===
          action.payload.settlement.session_id
        ) {
          state.selectedSession = {
            ...state.selectedSession,
            status: 'settled',
            settled_at: new Date().toISOString(),
          };
        }
      })
      .addCase(settleStarlineSession.rejected, (state, action) => {
        state.settling = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearStarlineResultError,
  clearStarlineResultSuccess,
  clearSelectedStarlineSession,
} = starlineResultSlice.actions;

export default starlineResultSlice.reducer;